import { useMutation, useQueryClient } from '@tanstack/react-query';
import { put } from '@/shared/api/api-client';
import { useAuthStore } from '@/auth/auth-store';
import type {
  AppointmentsDetailsResponse,
  AppointmentStatus,
  UpdateAppointmentStatusCommand,
} from '@/types/scheduling-api';
import { APPOINTMENT_LIST_QUERIES } from './appointment-list-keys';

/**
 * PUT /api/Appointment/Status — change an appointment's status code.
 *
 * Mirrors Flutter's `updateStatus` (scheduling_data_source_impl.dart:373).
 * Body shape matches `UpdateStatusRequest`:
 *   { appointmentId, updatedBy, status: { code, display, definition } }
 *
 * Optimistic update: writes the new status into every cached
 * AppointmentsDetailsResponse via setQueryData so the row reflects
 * immediately. The SignalR `UpdateAppointments` push from the server
 * follows shortly and reconciles any drift via the same merge path.
 */
export function useUpdateAppointmentStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (args: { appointmentId: string; status: AppointmentStatus }) => {
      // Empty-string Guid here triggers a .NET model-binder 400 (see
      // CLAUDE.md "API wire-shape contract"). The user should always be
      // authenticated when this mutation fires; throwing surfaces the
      // bug via the global error toast instead of a silent 400.
      const userId = useAuthStore.getState().user?.userId;
      if (!userId) {
        throw new Error('Cannot update appointment status: no authenticated user');
      }
      const command: UpdateAppointmentStatusCommand = {
        appointmentId: args.appointmentId,
        updatedBy: userId,
        status: args.status,
      };
      return put<string>('/api/Appointment/Status', command);
    },
    onMutate: ({ appointmentId, status }) => {
      // Snapshot every appointments-cache key so we can roll back on error.
      const snapshots: { key: readonly unknown[]; data: unknown }[] = [];
      queryClient
        .getQueryCache()
        .findAll(APPOINTMENT_LIST_QUERIES)
        .forEach((q) => {
          snapshots.push({ key: q.queryKey, data: q.state.data });
        });

      // Apply the optimistic status to every cached response that contains
      // this appointment id.
      queryClient.setQueriesData<AppointmentsDetailsResponse | undefined>(
        APPOINTMENT_LIST_QUERIES,
        (prev) => {
          if (!prev?.appointments) return prev;
          const idx = prev.appointments.findIndex((a) => a.id === appointmentId);
          if (idx < 0) return prev;
          const next = prev.appointments.slice();
          next[idx] = { ...next[idx], status };
          return recountSummary({ ...prev, appointments: next });
        },
      );

      return { snapshots };
    },
    onError: (_err, _vars, ctx) => {
      // Roll back every cache key we touched.
      ctx?.snapshots.forEach(({ key, data }) =>
        queryClient.setQueryData(key, data),
      );
    },
  });
}

/**
 * Recompute the summary counts (waitingRoom / noShow / cancelled) so the
 * filter pills don't drift after an optimistic status change.
 */
function recountSummary(
  response: AppointmentsDetailsResponse,
): AppointmentsDetailsResponse {
  let waitingRoom = 0;
  let noShow = 0;
  let cancelled = 0;
  for (const a of response.appointments ?? []) {
    const code = a.status?.code?.toLowerCase();
    if (code === 'checkedin') waitingRoom++;
    else if (code === 'noshow') noShow++;
    else if (code === 'cancelled') cancelled++;
  }
  return { ...response, waitingRoom, noShow, cancelled };
}
